/**
 * Over-the-air updates (ADR 0013): the expo-updates protocol v1 manifest, and the body the release
 * workflow sends to publish one. The manifest is signed by the publisher; the server stores and serves
 * the exact signed string and verifies the signature before accepting it.
 */
import { z } from "zod";
export const UpdatePlatform = z.enum(["android", "ios"]);
/** Base64url SHA-256, as expo-updates computes it. */
export const AssetHash = z.string().regex(/^[A-Za-z0-9_-]{43}$/, "base64url SHA-256");
export const ManifestAsset = z.object({
    hash: AssetHash,
    key: z.string().min(1).max(128),
    contentType: z.string().min(1).max(128),
    fileExtension: z.string().max(16).optional(),
    url: z.url(),
});
export const UpdateManifest = z.object({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    runtimeVersion: z.string().min(1).max(128),
    launchAsset: ManifestAsset,
    assets: z.array(ManifestAsset).max(2000),
    /** Carries the platform so a signed Android manifest cannot be filed under iOS. */
    metadata: z.looseObject({ platform: UpdatePlatform }),
    extra: z.record(z.string(), z.unknown()).optional(),
});
export const PublishUpdateBody = z.object({
    /** UpdateManifest serialized as JSON: the exact bytes that were signed. */
    manifest: z.string().min(2).max(2_000_000),
    /** Base64 RSASSA-PKCS1-v1_5 SHA-256 signature of `manifest`. */
    signature: z.string().min(16).max(4096),
    /** File contents for assets the server may not have yet (base64). */
    assets: z
        .array(z.object({ hash: AssetHash, contentType: z.string().min(1).max(128), data: z.string() }))
        .max(2000)
        .default([]),
});
export const PublishUpdateResponse = z.object({
    id: z.uuid(),
    /** False when this exact update had already been published. */
    created: z.boolean(),
});
/** Hashes among `hashes` that the server does not have yet, so the publisher uploads only those. */
export const MissingAssetsBody = z.object({ hashes: z.array(AssetHash).max(2000) });
export const MissingAssetsResponse = z.object({ missing: z.array(AssetHash) });
//# sourceMappingURL=updates.js.map